import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Edit2, Trash2, Plus, History } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useCategories, useDeleteCategory } from '../../hooks/useCategories';
import CategoryForm from './CategoryForm';
import SubcategoryManager from './SubcategoryManager';
import AuditDrawer from '../audit/AuditDrawer';
import LoadingSpinner from '../common/LoadingSpinner';
import type { Category } from '../../types/category';

const CategoryList: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [auditDrawer, setAuditDrawer] = useState<{
    isOpen: boolean;
    categoryId: string;
    categoryName: string;
  }>({
    isOpen: false,
    categoryId: '',
    categoryName: '',
  });

  const { data: categories = [], isLoading } = useCategories();
  const deleteCategory = useDeleteCategory();

  const handleCreate = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (categoryToDelete) {
      await deleteCategory.mutateAsync(categoryToDelete.categoryId);
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
    }
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleViewHistory = (category: Category) => {
    setAuditDrawer({
      isOpen: true,
      categoryId: category.categoryId,
      categoryName: category.name,
    });
  };

  const handleCloseHistory = () => {
    setAuditDrawer({
      isOpen: false,
      categoryId: '',
      categoryName: '',
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Categorías de Gastos</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gestiona las categorías y configura qué activos se pueden asociar a cada una
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Categoría
        </Button>
      </div>

      {categories.length === 0 ? (
        <div className="bg-slate-50 rounded-lg p-8 text-center">
          <p className="text-slate-600">No hay categorías creadas</p>
          <Button onClick={handleCreate} className="mt-4">
            Crear primera categoría
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {categories.map((category) => {
            const isExpanded = expandedCategories.has(category.categoryId);
            const hasCustomFields = category.customFields && category.customFields.length > 0;

            return (
              <div
                key={category.categoryId}
                className="bg-white rounded-lg shadow border border-gray-200"
              >
                {/* Category Header */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <button
                        onClick={() => toggleCategory(category.categoryId)}
                        className="p-1 hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-gray-600" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-600" />
                        )}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-base font-semibold text-gray-900">
                            {category.name}
                          </h3>
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              category.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {category.isActive ? 'Activa' : 'Inactiva'}
                          </span>
                          {hasCustomFields && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              🚗 {category.customFields!.length} campo{category.customFields!.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {category.description && (
                          <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>
                            {category.subcategories?.length || 0} subcategoría
                            {category.subcategories?.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewHistory(category)}
                        title="Ver historial"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(category)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(category)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expanded Content - Subcategories */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4">
                    <SubcategoryManager categoryId={category.categoryId} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
          </DialogHeader>
          <CategoryForm
            category={editingCategory || undefined}
            onSubmit={handleCloseForm}
            onCancel={handleCloseForm}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-gray-700">
              ¿Estás seguro de que deseas eliminar la categoría{' '}
              <span className="font-semibold">{categoryToDelete?.name}</span>?
            </p>
            {categoryToDelete?.subcategories && categoryToDelete.subcategories.length > 0 && (
              <p className="text-sm text-amber-600 text-center">
                Esta categoría tiene {categoryToDelete.subcategories.length} subcategoría(s) que también serán desactivadas.
              </p>
            )}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deleteCategory.isLoading}
              >
                {deleteCategory.isLoading ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Drawer */}
      <AuditDrawer
        entityType="CATEGORY"
        entityId={auditDrawer.categoryId}
        entityName={auditDrawer.categoryName}
        isOpen={auditDrawer.isOpen}
        onClose={handleCloseHistory}
      />
    </div>
  );
};

export default CategoryList;
